import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Newspaper,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  X,
  Save,
  Image
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminNews = () => {
  const { token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: 'General',
    summary: '',
    content: '',
    featured_image: '',
    publish_status: 'draft'
  });

  const categories = ['General', 'Programs', 'Facilities', 'Services', 'Emergency', 'Community'];

  const fetchNews = async () => {
    try {
      const response = await fetch(`${API}/api/admin/news`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNews(data);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
      toast.error('Failed to load news');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingNews 
        ? `${API}/api/admin/news/${editingNews.id}`
        : `${API}/api/admin/news`;
      
      const response = await fetch(url, {
        method: editingNews ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingNews ? 'News updated' : 'News created');
        setShowModal(false);
        setEditingNews(null);
        resetForm();
        fetchNews();
      } else {
        throw new Error('Operation failed');
      }
    } catch (error) {
      toast.error('Failed to save news');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this news article?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/news/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('News deleted');
        fetchNews();
      }
    } catch (error) {
      toast.error('Failed to delete news');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'General',
      summary: '',
      content: '',
      featured_image: '',
      publish_status: 'draft'
    });
  };

  const openEditModal = (item) => {
    setEditingNews(item);
    setFormData({
      title: item.title,
      category: item.category,
      summary: item.summary,
      content: item.content,
      featured_image: item.featured_image || '',
      publish_status: item.publish_status
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingNews(null);
    resetForm();
    setShowModal(true);
  };

  const filteredNews = news.filter(item =>
    item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-700',
      draft: 'bg-yellow-100 text-yellow-700',
      unpublished: 'bg-slate-100 text-slate-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <Newspaper className="w-7 h-7 text-primary" />
              News Management
            </h1>
            <p className="text-slate-600">Create and manage news articles</p>
          </div>
          <Button onClick={openCreateModal} className="bg-primary hover:bg-primary-700">
            <Plus className="w-4 h-4 mr-2" />
            Add News
          </Button>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search news..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="news-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* News Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full p-8 text-center text-slate-500">Loading...</div>
          ) : filteredNews.length === 0 ? (
            <div className="col-span-full p-8 text-center text-slate-500">No news found</div>
          ) : (
            filteredNews.map((item) => (
              <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-slate-100 relative">
                  {item.featured_image ? (
                    <img 
                      src={item.featured_image} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${getStatusBadge(item.publish_status)}`}>
                    {item.publish_status}
                  </span>
                </div>
                <CardContent className="p-4">
                  <span className="text-xs text-primary font-medium">{item.category}</span>
                  <h3 className="font-semibold text-slate-800 mt-1 line-clamp-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{item.summary}</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => openEditModal(item)} data-testid={`edit-news-${item.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-news-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingNews ? 'Edit News' : 'Create News'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      data-testid="news-title-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category</Label>
                      <select
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        data-testid="news-category-select"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        value={formData.publish_status}
                        onChange={(e) => setFormData({...formData, publish_status: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        data-testid="news-status-select"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="unpublished">Unpublished</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="featured_image">Featured Image URL</Label>
                    <Input
                      id="featured_image"
                      value={formData.featured_image}
                      onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                      placeholder="https://..."
                      data-testid="news-image-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="summary">Summary *</Label>
                    <textarea
                      id="summary"
                      value={formData.summary}
                      onChange={(e) => setFormData({...formData, summary: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      rows={2}
                      required
                      data-testid="news-summary-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="content">Content *</Label>
                    <textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      rows={6}
                      required
                      data-testid="news-content-input"
                    />
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="bg-primary hover:bg-primary-700" data-testid="news-save-btn">
                      <Save className="w-4 h-4 mr-2" />
                      {editingNews ? 'Update' : 'Create'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNews;
